// src/hooks/useTransactions.js
import { useState } from "react";
import { ethers } from "ethers";
import { validateAmount } from "../utils/validators";

// Import the depositFundTokens function from web3Service
// You'll need to create this function in your web3Service.js file
import { depositFundTokens } from "../services/web3Service";

export const useTransactions = (web3, fundData) => {
  const { contract, setError: setWeb3Error } = web3;
  const { fetchAllData, setError: setFundError } = fundData;
  
  const [isLoading, setIsLoading] = useState(false);
  const [inputAmount, setInputAmount] = useState("0.1");
  const [tokenType, setTokenType] = useState('ETH'); // Add token type state
  
  const setError = (error) => {
    setWeb3Error(error);
    setFundError(error);
  };

  const executeTransaction = async (operation, transactionFunction) => {
    setIsLoading(true);
    setError("");
    
    try {
      // Get the transaction object
      const tx = await transactionFunction();
      console.log(`${operation} Transaction Sent:`, tx);
      
      // Wait for it to be confirmed
      const receipt = await tx.wait();
      console.log(`${operation} Transaction Confirmed`);
      
      // Refresh data
      await fetchAllData();
      
      // Return the original transaction object that has the hash
      return tx;  // Return tx instead of { success: true }
    } catch (error) {
      console.error(`❌ ${operation} Failed:`, error);
      
      // Handle user rejection specifically
      if (error.code === "ACTION_REJECTED" || 
          error.code === 4001 || // MetaMask rejection code
          (error.message && error.message.includes("user rejected transaction"))) {
        // Special case for user cancellation
        console.log("User rejected the transaction");
        const cancelError = new Error("Transaction canceled by user");
        cancelError.code = "ACTION_REJECTED";
        cancelError.isCancelled = true; // Add a flag to identify cancellations
        throw cancelError;
      }
      
      // Handle other error types
      if (error.reason) {
        setError(`Transaction failed: ${error.reason}`);
      } else if (error.message) {
        setError(`Transaction failed: ${error.message}`);
      } else {
        setError(`Transaction failed. Check console for details.`);
      }
      
      throw error;  // Re-throw
    } finally {
      setIsLoading(false);
    }
  };

  const depositETH = async () => {
    if (!contract || isLoading) return { success: false };
    
    const validation = validateAmount(inputAmount);
    if (!validation.valid) {
      setError(validation.message);
      return { success: false };
    }

    return executeTransaction("Deposit", async () => {
      const amount = ethers.utils.parseEther(inputAmount);
      return contract.deposit({ value: amount });
    });
  };

  const withdrawETH = async () => {
    if (!contract || isLoading) return { success: false };
    
    const validation = validateAmount(inputAmount);
    if (!validation.valid) {
      setError(validation.message);
      return { success: false };
    }

    return executeTransaction("Withdraw", async () => {
      const amount = ethers.utils.parseEther(inputAmount);
      return contract.withdrawDeposit(amount);
    });
  };

  const mintShares = async () => {
    if (!contract || isLoading) return { success: false };

    return executeTransaction("Mint Shares", async () => {
      return contract.mintShares();
    });
  };

  const withdrawTokens = async () => {
    if (!contract || isLoading) return { success: false };
    
    const validation = validateAmount(inputAmount);
    if (!validation.valid) {
      setError(validation.message);
      return { success: false };
    }

    return executeTransaction("Withdraw Tokens", async () => {
      const amount = ethers.utils.parseEther(inputAmount);
      return contract.withdrawIndexTokens(amount);
    });
  };

  const redeemFundTokens = async () => {
    if (!contract || isLoading) return { success: false };
    
    const validation = validateAmount(inputAmount);
    if (!validation.valid) {
      setError(validation.message);
      return { success: false };
    }

    return executeTransaction("Redeem Fund Tokens", async () => {
      const amount = ethers.utils.parseEther(inputAmount);
      return contract.redeemFundTokens(amount);
    });
  };

  const redeemWalletTokens = async () => {
    if (!contract || isLoading) return { success: false };
    
    const validation = validateAmount(inputAmount);
    if (!validation.valid) {
      setError(validation.message);
      return { success: false };
    }

    return executeTransaction("Redeem Wallet Tokens", async () => {
      const amount = ethers.utils.parseEther(inputAmount);
      return contract.redeemPersonalTokens(amount);
    });
  };

  // New function to deposit fund tokens back to the fund
  const depositTokens = async () => {
    if (!contract || isLoading) return { success: false };
    
    const validation = validateAmount(inputAmount);
    if (!validation.valid) {
      setError(validation.message);
      return { success: false };
    }

    return executeTransaction("Deposit Fund Tokens", async () => {
      // Get proper signer from web3 object
      const { signer } = web3;
      if (!signer) {
        throw new Error("Signer not available");
      }
      
      // Use the depositFundTokens utility function to handle this operation
      return depositFundTokens(contract, signer, inputAmount);
    });
  };

  // New function to redeem fund tokens for a specific token by index
  const redeemTokenByIndex = async (tokenIndex, amount) => {
    if (!contract || isLoading) return { success: false };
    
    const validation = validateAmount(amount);
    if (!validation.valid) {
      setError(validation.message);
      return { success: false };
    }

    return executeTransaction(`Redeem Token by Index ${tokenIndex}`, async () => {
      const amountInWei = ethers.utils.parseEther(amount);
      return contract.redeemTokenByIndex(amountInWei, tokenIndex);
    });
  };

  return {
    isLoading,
    inputAmount,
    setInputAmount,
    tokenType,           // Add token type to returned state
    setTokenType,        // Add setter for token type
    depositETH,
    withdrawETH,
    mintShares,
    withdrawTokens,
    redeemFundTokens,
    redeemWalletTokens,
    depositTokens,       // Add new depositTokens function
    redeemTokenByIndex
  };
};